import { trackConversion } from "@/lib/analytics/gtag";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { PageShell, PageHero } from "@/components/PageShell";
import { SITE } from "@/lib/site";

export const Route = createFileRoute("/back-and-neck-assessment")({
  component: Assessment,
  head: () => ({
    meta: [
      { title: "Free Back & Neck Pain Risk Assessment, N S Physiotherapy" },
      {
        name: "description",
        content:
          "A free online screening tool to estimate your risk of persistent back or neck pain and guide early physiotherapy intervention.",
      },
      { property: "og:title", content: "Free Back & Neck Pain Risk Assessment" },
      { property: "og:url", content: "/back-and-neck-assessment" },
    ],
    links: [{ rel: "canonical", href: "/back-and-neck-assessment" }],
  }),
});

const QUESTIONS = [
  {
    q: "How long have you had pain?",
    options: [["Less than 2 weeks", 0], ["2 to 6 weeks", 1], ["More than 6 weeks", 2]] as const,
  },
  {
    q: "How intense is the pain right now (0 to 10)?",
    options: [["0 to 3", 0], ["4 to 6", 1], ["7 to 10", 2]] as const,
  },
  {
    q: "Does the pain radiate into a limb?",
    options: [["No", 0], ["Sometimes", 1], ["Yes, frequently", 2]] as const,
  },
  {
    q: "Any numbness, tingling or weakness?",
    options: [["No", 0], ["Mild", 1], ["Marked", 2]] as const,
  },
  {
    q: "Does pain wake you up at night?",
    options: [["No", 0], ["Sometimes", 1], ["Most nights", 2]] as const,
  },
  {
    q: "How much does it limit daily activity?",
    options: [["Not much", 0], ["Moderately", 1], ["A lot", 2]] as const,
  },
];

type Risk = "low" | "medium" | "high";

const PRICES: Record<Risk, { normal: number; discount: number; sessions: number }> = {
  low: { normal: 600, discount: 0, sessions: 2 },
  medium: { normal: 600 * 6, discount: 0.2, sessions: 6 },
  high: { normal: 600 * 12, discount: 0.3, sessions: 12 },
};

function rand(n: number) {
  return "R" + n.toLocaleString("en-ZA", { maximumFractionDigits: 0 });
}

function Assessment() {
  const [step, setStep] = useState<"intro" | "quiz" | "result">("intro");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [nameErr, setNameErr] = useState<string | null>(null);
  const [emailErr, setEmailErr] = useState<string | null>(null);
  const [answers, setAnswers] = useState<(number | null)[]>(Array(QUESTIONS.length).fill(null));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const score = answers.reduce<number>((a, b) => a + (b ?? 0), 0);
  const done = answers.every((a) => a !== null);
  const risk: Risk = score <= 3 ? "low" : score <= 7 ? "medium" : "high";

  async function startQuiz() {
    let ok = true;
    if (!fullName.trim()) {
      setNameErr("Please enter your full name.");
      ok = false;
    } else setNameErr(null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setEmailErr("Please enter a valid email address.");
      ok = false;
    } else setEmailErr(null);
    if (ok) setStep("quiz");
  }

  async function submitQuiz() {
    setSaving(true);
    setSaveError(null);
    try {
      const res = await fetch("/api/public/assessments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: fullName.trim(),
          email: email.trim(),
          area: "back_neck",
          answers: answers.map((a) => a ?? 0),
          score,
          risk_level: risk,
        }),
      });
      if (!res.ok) {
        setSaveError("We couldn't save your assessment, but you can still view your result.");
      }
    } catch {
      setSaveError("We couldn't save your assessment, but you can still view your result.");
    } finally {
      setSaving(false);
      trackConversion("assessment", "assessment_completed", {
        risk_level: risk,
        assessment_score: score,
        email: email.trim(),
        name: fullName.trim(),
      });
      setStep("result");
    }
  }

  return (
    <PageShell>
      <PageHero
        eyebrow="Free screening tool"
        title="Back and Neck Pain Risk Assessment"
        subtitle="A quick online screening to estimate your risk of persistent pain and guide early physiotherapy care."
      />
      <section className="container-page py-16 max-w-2xl bg-background">
        {step === "intro" && (
          <div className="space-y-6">
            <div className="rounded-2xl border border-border bg-card p-6">
              <h2 className="font-display text-xl font-semibold text-primary">What this assessment is for</h2>
              <ul className="mt-3 space-y-2 text-sm text-foreground/85 list-disc pl-5">
                <li>Identify your risk of developing persistent back or neck pain.</li>
                <li>Estimate the likelihood of long term disability if left untreated.</li>
                <li>Highlight people who may be at greater risk of their condition worsening.</li>
                <li>Encourage early physiotherapy to improve recovery and reduce the likelihood of future complications, including surgery.</li>
              </ul>
              <p className="mt-4 text-xs text-muted-foreground">
                This is a screening tool only. It does not diagnose medical conditions and does not guarantee whether surgery will or will not be required. It does not replace a professional physiotherapy assessment.
              </p>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                startQuiz();
              }}
              className="rounded-2xl border border-border bg-card p-6 space-y-4"
            >
<div>
                <label className="block text-sm font-semibold text-primary" htmlFor="assess-name">Full name</label>
                <input
                  id="assess-name"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  placeholder="Your full name"
                  autoComplete="name"
                />
                {nameErr && <p className="mt-1 text-xs text-destructive">{nameErr}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-primary" htmlFor="assess-email">Email address</label>
                <input
                  id="assess-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  placeholder="you@example.com"
                  autoComplete="email"
                />
                {emailErr && <p className="mt-1 text-xs text-destructive">{emailErr}</p>}
              </div>
              <button
                type="submit"
                className="btn-hero inline-flex h-12 items-center rounded-full px-6 font-semibold"
              >
                Start assessment
              </button>
              <p className="text-xs text-muted-foreground">
                Your details are stored securely so we can follow up with helpful information about your result.
              </p>
            </form>
          </div>
        )}

        {step === "quiz" && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (done) submitQuiz();
            }}
            className="space-y-6"
          >
            {QUESTIONS.map((item, qi) => (
              <fieldset key={qi} className="rounded-2xl border border-border bg-card p-5">
                <legend className="px-2 font-display font-semibold text-primary">
                  {qi + 1}. {item.q}
                </legend>
                <div className="mt-3 grid gap-2 sm:grid-cols-3">
{item.options.map(([label, val]) => {
                    const radioId = `assess-q-${qi}-${val}`;
                    return (
                      <label
                        key={label}
                        htmlFor={radioId}
                        className={`cursor-pointer rounded-lg border p-3 text-sm text-center transition ${
                          answers[qi] === val
                            ? "border-primary bg-accent text-primary font-semibold"
                            : "border-border hover:bg-accent"
                        }`}
                      >
                        <input
                          id={radioId}
                          type="radio"
                          name={`q-${qi}`}
                          className="sr-only"
                          checked={answers[qi] === val}
                          onChange={() =>
                            setAnswers((a) => a.map((x, i) => (i === qi ? val : x)))
                          }
                        />
                        {label}
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            ))}
            <button
              disabled={!done || saving}
              className="btn-hero inline-flex h-12 items-center rounded-full px-6 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Saving..." : "See my result"}
            </button>
          </form>
        )}

        {step === "result" && (
          <ResultView
            risk={risk}
            fullName={fullName}
            saveError={saveError}
            onRetake={() => {
              setAnswers(Array(QUESTIONS.length).fill(null));
              setStep("quiz");
            }}
          />
        )}
      </section>
    </PageShell>
  );
}

function ResultView({
  risk,
  fullName,
  saveError,
  onRetake,
}: {
  risk: Risk;
  fullName: string;
  saveError: string | null;
  onRetake: () => void;
}) {
  const label = risk === "low" ? "Low Risk" : risk === "medium" ? "Medium Risk" : "High Risk";
  const sessions =
    risk === "low"
      ? "1 to 2 physiotherapy sessions"
      : risk === "medium"
      ? "Approximately 6 physiotherapy sessions"
      : "Approximately 12 physiotherapy sessions";
  const message =
    risk === "low"
      ? "Your results indicate a low risk of persistent disability. Physiotherapy can help resolve your symptoms quickly and reduce the chance of recurrence."
      : risk === "medium"
      ? "Your results indicate a moderate risk of developing ongoing pain and functional limitations. A structured physiotherapy programme is recommended to reduce pain, improve movement and prevent your condition from worsening."
      : "Your assessment indicates a high risk of persistent pain, disability and future deterioration. Early physiotherapy intervention is strongly recommended to improve function, reduce pain and lower the likelihood of requiring surgery in the future.";

  const waText =
    risk === "low"
      ? "Hi, I completed your Back/Neck Pain Risk Assessment and scored Low Risk. I would like to know more about your physiotherapy treatment options."
      : risk === "medium"
      ? "Hi, I completed your Back/Neck Pain Risk Assessment and scored Medium Risk. I would like to know more about your 6 session physiotherapy package."
      : "Hi, I completed your Back/Neck Pain Risk Assessment and scored High Risk. I would like to know more about your 12 session physiotherapy package.";
  const waUrl = `https://wa.me/${SITE.whatsappIntl}?text=${encodeURIComponent(waText)}`;

  const pkg = PRICES[risk];
  const discountAmount = Math.round(pkg.normal * pkg.discount);
  const total = pkg.normal - discountAmount;

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-border bg-card p-8 text-center">
        <div className="text-xs font-semibold uppercase tracking-widest text-primary/70">
          {fullName ? `${fullName.split(" ")[0]}, your risk level` : "Your risk level"}
        </div>
        <div className="mt-2 font-display text-4xl font-bold text-primary">{label}</div>
        <p className="mt-2 text-sm font-medium text-foreground/80">Estimated treatment: {sessions}</p>
        <p className="mt-4 text-foreground/85">{message}</p>
        <p className="mt-4 text-xs text-muted-foreground">
          This is an estimate only. Your final treatment plan will be determined after a full physiotherapy assessment.
        </p>
        {saveError && (
          <p className="mt-3 text-xs text-destructive">{saveError}</p>
        )}
      </div>

      <div className="rounded-3xl border border-border bg-card p-6 sm:p-8">
        <h3 className="font-display text-xl font-semibold text-primary">Treatment options</h3>

        <div className="mt-4 rounded-2xl border border-border p-5">
          <p className="text-sm font-semibold text-primary">Medical aid patients</p>
          <p className="mt-1 text-sm text-foreground/85">
            Medical aid patients will be charged according to their medical scheme rates and available benefits.
          </p>
        </div>

        {risk !== "low" && (
          <div className="mt-4 rounded-2xl border border-primary/30 bg-accent/40 p-5">
            <p className="text-sm font-semibold text-primary">
              Cash patients, {pkg.sessions} session package
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Recommended for {risk === "medium" ? "Medium" : "High"} Risk patients
            </p>
            <dl className="mt-3 space-y-1 text-sm">
              <div className="flex justify-between">
                <dt className="text-foreground/70">Normal price</dt>
                <dd className="font-medium line-through text-foreground/70">{rand(pkg.normal)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-foreground/70">Discount applied</dt>
                <dd className="font-medium text-primary">
                  {Math.round(pkg.discount * 100)}% off ({rand(discountAmount)})
                </dd>
              </div>
              <div className="flex justify-between border-t border-border pt-2">
                <dt className="font-semibold text-primary">Package total</dt>
                <dd className="font-display text-lg font-bold text-primary">{rand(total)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-foreground/70">You save</dt>
                <dd className="font-medium text-primary">{rand(discountAmount)}</dd>
              </div>
            </dl>
          </div>
        )}
      </div>

      <div className="flex flex-wrap justify-center gap-3">
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-hero inline-flex h-12 items-center rounded-full px-6 font-semibold"
        >
          Chat on WhatsApp
        </a>
        <a
          href="/booking"
          className="inline-flex h-12 items-center rounded-full border border-primary bg-background px-6 font-semibold text-primary hover:bg-accent"
        >
          Book an appointment
        </a>
        <button
          onClick={onRetake}
          className="inline-flex h-12 items-center rounded-full border border-border px-6 font-semibold text-primary hover:bg-accent"
        >
          Take again
        </button>
      </div>
    </div>
  );
}
